import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, PlayCircle, CheckCircle2, RefreshCw, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useDesign } from '@/contexts/DesignContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function KBMaintenance({ adminQS }) {
  const { isStudio } = useDesign();
  const [log, setLog] = useState('');
  const [running, setRunning] = useState(false);
  const pollRef = useRef(null);

  const fetchLog = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai/knowledge-base/job-log?${adminQS}`);
      const data = await res.json();
      const text = data.log || '';
      setLog(text);
      if (/\[exit code: \d+\]|\[JOB FAILED|\[index rebuilt/.test(text)) {
        setRunning(false);
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (running) {
      pollRef.current = setInterval(fetchLog, 4000);
      return () => clearInterval(pollRef.current);
    }
    clearInterval(pollRef.current);
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (path, label, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      const res = await fetch(`${API_URL}/api/ai/${path}${path.includes('?') ? '&' : '?'}${adminQS}`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success(`${label} started — progress appears below`);
      setLog('Starting...');
      setRunning(true);
      setTimeout(fetchLog, 2500);
    } catch (e) {
      toast.error(`${label} failed: ${e.message}`);
    }
  };

  return (
    <div className={isStudio ? 'st31-card' : 'bg-card border border-slate-200 dark:border-white/10 rounded-xl p-4'} data-testid="kb-maintenance-card">
      {isStudio
        ? <div className="st31-ch"><h3 className="st31-ct">Knowledge base maintenance</h3></div>
        : <div className="flex items-center gap-2 font-semibold text-sm text-slate-900 dark:text-white mb-1">
            <Sparkles className="w-4 h-4 text-amber-500" /> Knowledge Base Maintenance
          </div>}
      <div className={isStudio ? 'st31-cb' : undefined}>
      <p className={isStudio ? 'st31-cap' : 'text-xs text-slate-500 dark:text-slate-400 mb-3'}>
        Run these in order after a deploy or big content import: 1) preview what cleanup would remove, 2) apply it, 3) rebuild the AI's search index. Each runs in the background; progress shows below.
        <span className="block mt-1">Safety: cleanup never deletes articles, it only flags them out of AI answers. "Undo Last Cleanup" fully restores the most recent run.</span>
      </p>
      <div className={isStudio ? 'st31-vars' : 'flex flex-wrap gap-2 mb-3'} style={isStudio ? { gap: 4 } : undefined}>
        {isStudio ? (<>
          <button type="button" className="st-btn st-ghost st-sm" data-testid="kb-dry-run-btn" disabled={running}
            onClick={() => start('knowledge-base/hygiene', 'Cleanup dry run')}><PlayCircle /> 1 · Preview cleanup</button>
          <button type="button" className="st-btn st-ghost st-sm" data-testid="kb-apply-btn" disabled={running}
            onClick={() => start('knowledge-base/hygiene?apply=true', 'Cleanup',
              'Apply cleanup? Flagged promo/duplicate articles will be excluded from AI answers (reversible, nothing is deleted). Run the dry run first and check the list below.')}><CheckCircle2 /> 2 · Apply cleanup</button>
          <button type="button" className="st-btn st-ghost st-sm" data-testid="kb-reembed-btn" disabled={running}
            onClick={() => start('knowledge-base/reembed', 'Re-embed',
              'Re-embed the knowledge base? This rebuilds the AI search index for all active articles (~10-15 min, ~$0.25 in OpenAI usage).')}><RefreshCw /> 3 · Re-embed and rebuild index</button>
          <button type="button" className="st-btn st-ghost st-sm" data-testid="kb-undo-btn" disabled={running} style={{ color: 'var(--st-bad)' }}
            onClick={async () => {
              if (!window.confirm('Undo the most recent applied cleanup? All articles it excluded will be restored and their search index rebuilt.')) return;
              try {
                const res = await fetch(`${API_URL}/api/ai/knowledge-base/hygiene/undo?${adminQS}`, { method: 'POST' });
                const data = await res.json();
                if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
                toast.success(data.message || `Restored ${data.restored_articles} articles`);
                setLog('Undo applied. Re-embedding restored articles...');
                setRunning(true);
                setTimeout(fetchLog, 2500);
              } catch (e) {
                toast.error(`Undo failed: ${e.message}`);
              }
            }}><Undo2 /> Undo last cleanup</button>
          <span className="st31-fsp" style={{ flex: 1 }} />
          <button type="button" className="st-lnk" data-testid="kb-log-refresh-btn" onClick={fetchLog}>Refresh log</button>
        </>) : (<>
        <Button size="sm" variant="outline" data-testid="kb-dry-run-btn" disabled={running}
          onClick={() => start('knowledge-base/hygiene', 'Cleanup dry run')}>
          <PlayCircle className="w-3.5 h-3.5 mr-1.5" /> 1. Preview Cleanup (dry run)
        </Button>
        <Button size="sm" variant="outline" data-testid="kb-apply-btn" disabled={running}
          onClick={() => start('knowledge-base/hygiene?apply=true', 'Cleanup',
            'Apply cleanup? Flagged promo/duplicate articles will be excluded from AI answers (reversible, nothing is deleted). Run the dry run first and check the list below.')}>
          <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> 2. Apply Cleanup
        </Button>
        <Button size="sm" variant="outline" data-testid="kb-reembed-btn" disabled={running}
          onClick={() => start('knowledge-base/reembed', 'Re-embed',
            'Re-embed the knowledge base? This rebuilds the AI search index for all active articles (~10-15 min, ~$0.25 in OpenAI usage).')}>
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> 3. Re-embed & Rebuild Index
        </Button>
        <Button size="sm" variant="outline" data-testid="kb-undo-btn" disabled={running}
          className="border-amber-300 dark:border-amber-500/40 text-amber-700 dark:text-amber-300"
          onClick={async () => {
            if (!window.confirm('Undo the most recent applied cleanup? All articles it excluded will be restored and their search index rebuilt.')) return;
            try {
              const res = await fetch(`${API_URL}/api/ai/knowledge-base/hygiene/undo?${adminQS}`, { method: 'POST' });
              const data = await res.json();
              if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
              toast.success(data.message || `Restored ${data.restored_articles} articles`);
              setLog('Undo applied. Re-embedding restored articles...');
              setRunning(true);
              setTimeout(fetchLog, 2500);
            } catch (e) {
              toast.error(`Undo failed: ${e.message}`);
            }
          }}>
          <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Undo Last Cleanup
        </Button>
        <Button size="sm" variant="ghost" data-testid="kb-log-refresh-btn" onClick={fetchLog}>
          Refresh log
        </Button>
        </>)}
      </div>
      {log && (
        <pre data-testid="kb-job-log" className={isStudio ? 'st31-log' : 'text-[11px] leading-relaxed bg-slate-50 dark:bg-black/30 border border-slate-100 dark:border-white/10 rounded-lg p-3 max-h-64 overflow-y-auto whitespace-pre-wrap text-slate-700 dark:text-slate-300'}>
          {running ? (isStudio ? '⏳ Job running · updates every few seconds\n\n' : '⏳ Job running — updates every few seconds...\n\n') : ''}{log}
        </pre>
      )}
      </div>
    </div>
  );
}
